import React, { useRef, useState } from 'react';

import { AutocompleteInputChangeReason, Stack, Typography } from '@mui/material';
import { useTranslation } from 'react-i18next';
import useSWRInfinite from 'swr/infinite';

import { AsyncAutocomplete, AsyncAutocompleteProps } from '~components';
import { OffsetPagedResults } from '~interfaces';
import i18n from '~lib/i18n';
import { toStreetString } from '~utils/addressUtils';

import Hub from '../../interfaces/hub';
import HubsService from '../../services/hubsService';

const service = new HubsService();

interface ItemAutocompleteProps extends Omit<AsyncAutocompleteProps<Hub>, 'options' | 'label'> {
	label?: string;
	count?: number;
	organisationId?: string;
}

const HubAutocomplete = ({
	label = i18n.t('ui.label.location'),
	count = 10,
	organisationId,
	...asyncAutocompleteProps
}: ItemAutocompleteProps) => {
	const { t } = useTranslation();

	const [searchQuery, setSearchQuery] = useState('');
	const [open, setOpen] = useState(false);

	// Let's set the on the first page response. Accept that it may happen
	// if an organisation is added in the mean time
	// Small changes right..?
	const total = useRef<number>();
	const page = useRef(1);

	const getKey = (i: number, previousData: OffsetPagedResults<Hub[]>) => {
		if (!open || (previousData && !previousData.hasMore)) {
			// reached the end
			return null;
		}

		page.current = i + 1;

		return [
			service.basePath,
			{
				page: page.current,
				pageSize: count,
				organisationId: organisationId,
				searchQuery: searchQuery || undefined,
			},
		] as const;
	};

	const { data, isLoading, isValidating, size, setSize } = useSWRInfinite(
		getKey,
		([_, args]) => service.getHubs(args),
		{
			keepPreviousData: true,
			revalidateFirstPage: false,
			onSuccess: (res) => (total.current = res[0].total),
		}
	);

	/**
	 * Just set the size of the pages
	 */
	const handleOverflow = () => {
		if (total.current != null && (size - 1) * count <= total.current) {
			setSize(size + 1);
		}
	};

	const handleInputChange = (
		e: React.SyntheticEvent<Element, Event>,
		value: string,
		reason: AutocompleteInputChangeReason
	) => {
		if (isLoading) {
			service.abortCurrentRequest('inputChange');
		}
		setSearchQuery(value);

		asyncAutocompleteProps.onInputChange?.(e, value, reason);
	};

	return (
		<AsyncAutocomplete
			{...asyncAutocompleteProps}
			delay={1000}
			label={label}
			loading={isLoading || isValidating}
			noOptionsText={t('noResults')}
			onOpen={() => setOpen(true)}
			onClose={() => setOpen(false)}
			options={data?.flatMap((el) => el.results) ?? []}
			onInputChange={handleInputChange}
			onOverflow={handleOverflow}
			// renderOption={(props, option) => {
			// 	const { key, ...optionProps } = props;

			// 	return (
			// 		<Stack component='li' {...optionProps} key={option.id} direction='row' spacing={1}>
			// 			{/* <Avatar src={option.imageUrl} /> */}
			// 			<Stack>
			// 				<Typography>{option.label}</Typography>
			// 				<Typography variant='body2'>
			// 					{option.address ? toStreetString(option.address) : '-'}
			// 				</Typography>
			// 			</Stack>
			// 		</Stack>
			// 	);
			// }}
		/>
	);
};

export default HubAutocomplete;
